import { useEffect, useState } from 'react';
import { BizButton } from '../../../Core/Components/Mini/BizButton';
import BizInput from '../../../Core/Components/Mini/BizInput';
import BizTextArea from '../../../Core/Components/Mini/BizTextArea';
import {
	useCreateTimereport,
	useUpdateTimereport,
	useDeleteTimereport,
} from '../../../Core/Hooks/useTimereport';
import { TimereportItem } from '../../../Core/Types/admin/timereport';
import { CreateTimereportRequest, UpdateTimereportRequest } from '../../../Core/Types/timereport';
import TimereportActivitySelector from './TimereportActivitySelector';
import './TimereportItemForm.scss';

type TimereportItemFormProps = {
	date: string;
	existingItem: TimereportItem | null;
	onClose: () => void;
};

const TimereportItemForm = ({ date, existingItem, onClose }: TimereportItemFormProps) => {
	const { create, statusState: createStatus } = useCreateTimereport();
	const { update, statusState: updateStatus } = useUpdateTimereport();
	const { remove, statusState: deleteStatus } = useDeleteTimereport();

	const [projectId, setProjectId] = useState(existingItem?.projectId ?? 0);
	const [activityId, setActivityId] = useState(existingItem?.activityId ?? 0);
	const [typeId, setTypeId] = useState(existingItem?.typeId ?? 0);
	const [hours, setHours] = useState(existingItem?.units ?? 0);
	const [comment, setComment] = useState(existingItem?.comment ?? '');

	const isEditing = existingItem !== null;
	const isSubmitted = existingItem?.isSubmitted ?? false;

	useEffect(() => {
		setProjectId(existingItem?.projectId ?? 0);
		setActivityId(existingItem?.activityId ?? 0);
		setTypeId(existingItem?.typeId ?? 0);
		setHours(existingItem?.units ?? 0);
		setComment(existingItem?.comment ?? '');
	}, [existingItem]);

	useEffect(() => {
		if (createStatus === 'SUCCESS' || updateStatus === 'SUCCESS' || deleteStatus === 'SUCCESS') {
			onClose();
		}
	}, [createStatus, updateStatus, deleteStatus, onClose]);

	const canSave = projectId > 0 && activityId > 0 && hours > 0 && !isSubmitted;

	const handleSave = () => {
		if (!canSave) return;

		if (isEditing) {
			const data: UpdateTimereportRequest = {
				id: existingItem.id,
				projectId,
				activityId,
				typeId,
				objectType: existingItem.objectType,
				objectId: existingItem.objectId,
				hours,
				date,
				comment,
			};
			update(data);
		} else {
			const data: CreateTimereportRequest = {
				projectId,
				activityId,
				typeId,
				objectType: null,
				objectId: null,
				hours,
				date,
				comment,
			};
			create(data);
		}
	};

	const handleDelete = () => {
		if (existingItem) {
			remove(existingItem.id);
		}
	};

	const handleActivitySelected = (selectedProjectId: number, selectedActivityId: number, selectedTypeId: number) => {
		setProjectId(selectedProjectId);
		setActivityId(selectedActivityId);
		if (selectedTypeId > 0) setTypeId(selectedTypeId);
	};

	const saveStatus = isEditing ? updateStatus : createStatus;

	return (
		<div className='biz-timereport-item-form'>
			<div className='biz-timereport-item-form__title'>
				{isEditing ? 'Edit Time Report' : 'New Time Report'} — {date.split('T')[0]}
			</div>

			<div className='biz-timereport-item-form__fields'>
				<TimereportActivitySelector
					selectedProjectId={projectId}
					selectedActivityId={activityId}
					onSelect={handleActivitySelected}
				/>

				<div className='biz-timereport-item-form__field'>
					<span className='biz-timereport-item-form__label'>Hours</span>
					<BizInput
						type='number'
						value={String(hours)}
						onChange={(e) => setHours(parseFloat(e.target.value) || 0)}
						min='0'
						max='24'
						readonly={isSubmitted}
					/>
				</div>

				<div className='biz-timereport-item-form__field'>
					<span className='biz-timereport-item-form__label'>Comment</span>
					<BizTextArea
						value={comment}
						onChange={(e) => setComment(e.target.value)}
						readonly={isSubmitted}
					/>
				</div>
			</div>

			<div className='biz-timereport-item-form__actions'>
				<BizButton
					text={isEditing ? 'Update' : 'Create'}
					onClick={handleSave}
					state={saveStatus}
					icon='far fa-save'
					disabled={!canSave}
				/>
				<BizButton text='Cancel' onClick={onClose} />
			</div>

			{isEditing && !isSubmitted && (
				<div className='biz-timereport-item-form__delete-section'>
					<BizButton
						text='Delete'
						onClick={handleDelete}
						state={deleteStatus}
						icon='far fa-times'
					/>
				</div>
			)}
		</div>
	);
};

export default TimereportItemForm;
